import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "../../components/ui/button";
import { useCreateCheckout } from "../../hooks/mutations/use-billing-mutations";
import { useBillingPlans } from "../../hooks/queries/use-billing";
import { BillingService } from "../../services/billing/billingService";
import type { BillingPlan } from "../../services/types";
import { useAuthStore } from "../../stores/auth-store";

export const Route = createFileRoute("/(public)/pricing")({
  component: PricingPage,
});

interface StaticPlan {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  interval: string;
  features: string[];
  limitations: string[];
  popular?: boolean;
}

function PricingPage() {
  const { t } = useTranslation("pricing");
  const { data: plans, isLoading, error } = useBillingPlans();
  const checkoutMutation = useCreateCheckout();
  const { isAuthenticated } = useAuthStore();

  const navigate = Route.useNavigate();

  // Static fallback plans when Stripe is not configured
  const staticPlans: StaticPlan[] = [
    {
      id: "free",
      name: t("plans.free.name"),
      description: t("plans.free.description"),
      amount: 0,
      currency: "usd",
      interval: "month",
      features: t("plans.free.features", { returnObjects: true }),
      limitations: t("plans.free.limitations", { returnObjects: true }),
    },
    {
      id: "pro",
      name: t("plans.pro.name"),
      description: t("plans.pro.description"),
      amount: 1900, // $19.00
      currency: "usd",
      interval: "month",
      features: t("plans.pro.features", { returnObjects: true }),
      limitations: t("plans.pro.limitations", { returnObjects: true }),
      popular: true,
    },
    {
      id: "enterprise",
      name: t("plans.enterprise.name"),
      description: t("plans.enterprise.description"),
      amount: 9900, // $99.00
      currency: "usd",
      interval: "month",
      features: t("plans.enterprise.features", { returnObjects: true }),
      limitations: t("plans.enterprise.limitations", { returnObjects: true }) as string[],
    },
  ];

  // Use Stripe plans if available, otherwise use static fallback
  const hasStripePlans = plans && plans.length > 0;
  const displayPlans = hasStripePlans ? plans : null;

  const handleSubscribe = (priceId: string) => {
    if (!isAuthenticated) {
      void navigate({ to: "/login", search: { redirect: "/pricing" } });
      return;
    }
    if (hasStripePlans) {
      checkoutMutation.mutate(priceId);
    } else {
      // For static plans, redirect to contact or register
      void navigate({ to: "/register" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-16">
      {/* Header */}
      <div className="mb-12 text-center">
        <h1 className="mb-4 text-4xl font-bold">{t("title")}</h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg">{t("subtitle")}</p>
      </div>

      {/* Pricing Cards */}
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {hasStripePlans ? (
          <>
            {/* Free Plan (always shown) */}
            <StaticPlanCard
              plan={staticPlans[0]}
              onAction={() => navigate({ to: "/register" })}
              actionLabel={t("buttons.getStarted")}
              variant="outline"
            />
            {/* Dynamic Plans from Stripe */}
            {displayPlans?.map((plan) => (
              <PricingCard
                key={plan.id}
                plan={plan}
                onSubscribe={() => handleSubscribe(plan.price_id)}
                isLoading={checkoutMutation.isPending}
                isAuthenticated={isAuthenticated}
              />
            ))}
          </>
        ) : (
          /* Static fallback plans */
          staticPlans.map((plan, index) => (
            <StaticPlanCard
              key={plan.id}
              plan={plan}
              onAction={() => {
                if (plan.id === "free") {
                  void navigate({ to: "/register" });
                } else if (plan.id === "enterprise") {
                  window.location.href = "mailto:sales@example.com?subject=Enterprise%20Plan%20Inquiry";
                } else {
                  void navigate({ to: "/register" });
                }
              }}
              actionLabel={
                plan.id === "free"
                  ? t("buttons.getStarted")
                  : plan.id === "enterprise"
                    ? t("buttons.contactSales")
                    : t("buttons.startTrial")
              }
              variant={plan.id === "free" ? "outline" : "default"}
            />
          ))
        )}
      </div>

      {/* Feature Comparison Table */}
      {!hasStripePlans && <ComparisonTable />}

      {/* FAQ Section */}
      <FaqSection />

      {/* Contact CTA */}
      <div className="mt-16 text-center">
        <p className="text-muted-foreground">
          {t("contact.moreQuestions")}{" "}
          <a
            href="mailto:support@example.com"
            className="text-primary hover:underline"
          >
            {t("contact.contactSales")}
          </a>
        </p>
      </div>
    </div>
  );
}

interface PricingCardProps {
  plan: BillingPlan;
  onSubscribe: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

function PricingCard({ plan, onSubscribe, isLoading, isAuthenticated }: PricingCardProps) {
  const { t } = useTranslation("pricing");
  const isPopular = plan.name.toLowerCase().includes("premium") || plan.name.toLowerCase().includes("pro");

  return (
    <div
      className={`relative rounded-2xl border p-8 transition-all duration-300 hover:shadow-xl ${
        isPopular
          ? "border-primary from-primary/10 to-primary/5 z-10 scale-[1.02] border-2 bg-gradient-to-b shadow-lg"
          : "bg-card hover:-translate-y-1"
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-primary text-primary-foreground rounded-full px-4 py-1.5 text-xs font-semibold shadow-md">
            {t("mostPopular")}
          </span>
        </div>
      )}

      <h3 className="mb-2 text-xl font-semibold">{plan.name}</h3>
      {plan.description && <p className="text-muted-foreground mb-4 text-sm">{plan.description}</p>}

      <div className="mb-6">
        <span className="text-4xl font-bold">{BillingService.formatPrice(plan.amount, plan.currency)}</span>
        <span className="text-muted-foreground">/{plan.interval}</span>
      </div>

      <ul className="mb-8 space-y-3">
        {plan.features && plan.features.length > 0 ? (
          plan.features.map((feature, index) => <FeatureItem key={index}>{feature}</FeatureItem>)
        ) : (
          <>
            <FeatureItem>{t("fallback.allFreeFeatures")}</FeatureItem>
            <FeatureItem>{t("fallback.prioritySupport")}</FeatureItem>
            <FeatureItem>{t("fallback.unlimitedProjects")}</FeatureItem>
            <FeatureItem>{t("fallback.advancedAnalytics")}</FeatureItem>
          </>
        )}
      </ul>

      <Button
        onClick={onSubscribe}
        loading={isLoading}
        variant={isPopular ? "glow" : "default"}
        className="w-full"
      >
        {isAuthenticated ? t("buttons.subscribeNow") : t("buttons.signUpToSubscribe")}
      </Button>
    </div>
  );
}

function FeatureItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2.5">
      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500/10">
        <Check className="h-3 w-3 text-green-600 dark:text-green-400" />
      </div>
      <span className="text-sm">{children}</span>
    </li>
  );
}

function LimitationItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2">
      <X className="text-muted-foreground h-4 w-4" />
      <span className="text-muted-foreground text-sm">{children}</span>
    </li>
  );
}

interface StaticPlanCardProps {
  plan: StaticPlan;
  onAction: () => void;
  actionLabel: string;
  variant?: "default" | "outline";
}

function StaticPlanCard({ plan, onAction, actionLabel, variant = "default" }: StaticPlanCardProps) {
  const { t } = useTranslation("pricing");

  return (
    <div
      className={`relative rounded-2xl border p-8 transition-all duration-300 hover:shadow-xl ${
        plan.popular
          ? "border-primary from-primary/10 to-primary/5 z-10 scale-[1.02] border-2 bg-gradient-to-b shadow-lg"
          : "bg-card hover:-translate-y-1"
      }`}
    >
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-primary text-primary-foreground rounded-full px-4 py-1.5 text-xs font-semibold shadow-md">
            {t("mostPopular")}
          </span>
        </div>
      )}

      <h3 className="mb-2 text-xl font-semibold">{plan.name}</h3>
      <p className="text-muted-foreground mb-4 text-sm">{plan.description}</p>

      <div className="mb-6">
        <span className="text-4xl font-bold">
          {plan.amount === 0 ? "$0" : BillingService.formatPrice(plan.amount, plan.currency)}
        </span>
        <span className="text-muted-foreground">/{plan.interval}</span>
      </div>

      <ul className="mb-4 space-y-2">
        {Array.isArray(plan.features) &&
          plan.features.map((feature, index) => <FeatureItem key={index}>{feature}</FeatureItem>)}
      </ul>

      {Array.isArray(plan.limitations) && plan.limitations.length > 0 && (
        <ul className="mb-6 space-y-2">
          {plan.limitations.map((limitation, index) => (
            <LimitationItem key={index}>{limitation}</LimitationItem>
          ))}
        </ul>
      )}

      <Button
        onClick={onAction}
        variant={plan.popular ? "glow" : variant}
        className="w-full"
      >
        {actionLabel}
      </Button>
    </div>
  );
}

interface ComparisonRowProps {
  feature: string;
  free: boolean | string;
  pro: boolean | string;
  enterprise: boolean | string;
}

function ComparisonRow({ feature, free, pro, enterprise }: ComparisonRowProps) {
  const renderValue = (value: boolean | string) => {
    if (typeof value === "boolean") {
      return value ? (
        <Check className="mx-auto h-5 w-5 text-green-500" />
      ) : (
        <X className="text-muted-foreground mx-auto h-5 w-5" />
      );
    }
    return <span className="text-sm">{value}</span>;
  };

  return (
    <tr className="border-b">
      <td className="p-4 text-left text-sm">{feature}</td>
      <td className="p-4 text-center">{renderValue(free)}</td>
      <td className="bg-primary/5 p-4 text-center">{renderValue(pro)}</td>
      <td className="p-4 text-center">{renderValue(enterprise)}</td>
    </tr>
  );
}

function ComparisonTable() {
  const { t } = useTranslation("pricing");

  return (
    <div className="mt-16">
      <h2 className="mb-8 text-center text-2xl font-bold">{t("comparison.title")}</h2>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b">
              <th className="p-4 text-left font-medium">{t("comparison.feature")}</th>
              <th className="p-4 text-center font-medium">{t("plans.free.name")}</th>
              <th className="bg-primary/5 p-4 text-center font-medium">{t("plans.pro.name")}</th>
              <th className="p-4 text-center font-medium">{t("plans.enterprise.name")}</th>
            </tr>
          </thead>
          <tbody>
            <ComparisonRow
              feature={t("comparison.features.projects")}
              free="3"
              pro={t("comparison.unlimited")}
              enterprise={t("comparison.unlimited")}
            />
            <ComparisonRow
              feature={t("comparison.features.storage")}
              free="1GB"
              pro="25GB"
              enterprise={t("comparison.unlimited")}
            />
            <ComparisonRow
              feature={t("comparison.features.apiRequests")}
              free="100/day"
              pro="10,000/day"
              enterprise={t("comparison.unlimited")}
            />
            <ComparisonRow
              feature={t("comparison.features.teamMembers")}
              free={false}
              pro="5"
              enterprise={t("comparison.unlimited")}
            />
            <ComparisonRow
              feature={t("comparison.features.customBranding")}
              free={false}
              pro={true}
              enterprise={true}
            />
            <ComparisonRow
              feature={t("comparison.features.prioritySupport")}
              free={false}
              pro={true}
              enterprise={true}
            />
            <ComparisonRow
              feature={t("comparison.features.twoFactorAuth")}
              free={true}
              pro={true}
              enterprise={true}
            />
            <ComparisonRow
              feature={t("comparison.features.ssoSaml")}
              free={false}
              pro={false}
              enterprise={true}
            />
            <ComparisonRow
              feature={t("comparison.features.auditLogs")}
              free={false}
              pro={false}
              enterprise={true}
            />
            <ComparisonRow
              feature={t("comparison.features.slaGuarantee")}
              free={false}
              pro={false}
              enterprise={true}
            />
            <ComparisonRow
              feature={t("comparison.features.dedicatedSupport")}
              free={false}
              pro={false}
              enterprise={true}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FaqSection() {
  const { t } = useTranslation("pricing");

  return (
    <div className="mt-16">
      <h2 className="mb-8 text-center text-2xl font-bold">{t("faq.title")}</h2>
      <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-2">
        <FaqItem
          question={t("faq.questions.changePlans.question")}
          answer={t("faq.questions.changePlans.answer")}
        />
        <FaqItem
          question={t("faq.questions.paymentMethods.question")}
          answer={t("faq.questions.paymentMethods.answer")}
        />
        <FaqItem
          question={t("faq.questions.freeTrial.question")}
          answer={t("faq.questions.freeTrial.answer")}
        />
        <FaqItem
          question={t("faq.questions.exceedLimits.question")}
          answer={t("faq.questions.exceedLimits.answer")}
        />
      </div>
    </div>
  );
}

interface FaqItemProps {
  question: string;
  answer: string;
}

function FaqItem({ question, answer }: FaqItemProps) {
  return (
    <div className="bg-card border-l-primary rounded-lg border border-l-4 p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <h3 className="mb-2 font-semibold">{question}</h3>
      <p className="text-muted-foreground text-sm">{answer}</p>
    </div>
  );
}
